"use strict";
/**
 * sl-pbux.1.1: Pure transformation from ViewConfig (flat ViewComponentInstance[])
 * to DocPageSnapshot (tree-based DocNodeModel) under a PageShapeRoot.
 *
 * Pure function: no Y.Doc, no I/O, no globals. Deterministic byte-stable
 * output for equivalent inputs. Idempotent: same input -> same output.
 *
 * Slot routing rules:
 *   - navigation components (custom-app-navigation, breadcrumb, search-bar, filter-bar, pagination)
 *     route to the chrome slot of the active shape:
 *       header  -> 'header'
 *       sidebar -> 'sidebar'
 *       fullwidth -> 'main'
 *   - everything else (content, data, widget, unknown custom types) -> 'main'.
 *
 * Component ordering: position.y asc, then position.x asc, then id asc.
 *
 * Tag mapping kept narrow: 'nav' for navigation kind, 'section' otherwise.
 * The PageShapeRoot is a 'div'; slot tags are resolved by the renderer per
 * SlotDocNode.name.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONTAINER_COMPONENT_TYPES = exports.PAGE_SHAPE_LAYOUT_CONTRACTS = exports.SLOT_IDS = exports.PAGE_SHAPE_ROOT_ID = exports.PAGE_SHAPES = void 0;
exports.getPageShapeLayoutContract = getPageShapeLayoutContract;
exports.classifyComponentKind = classifyComponentKind;
exports.routeToSlot = routeToSlot;
exports.routeComponentToSlot = routeComponentToSlot;
exports.parsePageShape = parsePageShape;
exports.viewConfigToDocPageSnapshot = viewConfigToDocPageSnapshot;
const formula_1 = require("./formula");
exports.PAGE_SHAPES = [
    'header',
    'sidebar',
    'fullwidth',
];
exports.PAGE_SHAPE_ROOT_ID = 'page-shape-root';
exports.SLOT_IDS = {
    header: 'slot-header',
    sidebar: 'slot-sidebar',
    main: 'slot-main',
    footer: 'slot-footer',
};
exports.PAGE_SHAPE_LAYOUT_CONTRACTS = {
    header: {
        shape: 'header',
        slots: ['header', 'main', 'footer'],
        navigationSlot: 'header',
        semanticTags: {
            header: 'header',
            sidebar: null,
            main: 'main',
            footer: 'footer',
        },
        desktopGridAreas: ['header', 'main', 'footer'],
        mobileGridAreas: ['header', 'main', 'footer'],
    },
    sidebar: {
        shape: 'sidebar',
        slots: ['header', 'sidebar', 'main', 'footer'],
        navigationSlot: 'sidebar',
        semanticTags: {
            header: 'header',
            sidebar: 'aside',
            main: 'main',
            footer: 'footer',
        },
        desktopGridAreas: ['header header', 'sidebar main', 'footer footer'],
        mobileGridAreas: ['header', 'sidebar', 'main', 'footer'],
    },
    fullwidth: {
        shape: 'fullwidth',
        slots: ['main', 'footer'],
        navigationSlot: 'main',
        semanticTags: {
            header: null,
            sidebar: null,
            main: 'main',
            footer: 'footer',
        },
        desktopGridAreas: ['main', 'footer'],
        mobileGridAreas: ['main', 'footer'],
    },
};
function getPageShapeLayoutContract(shape) {
    return exports.PAGE_SHAPE_LAYOUT_CONTRACTS[shape];
}
const COMPONENT_KIND_BY_TYPE = {
    // Navigation — routed to chrome slot per shape
    breadcrumb: 'navigation',
    'search-bar': 'navigation',
    'filter-bar': 'navigation',
    pagination: 'navigation',
    'language-switcher': 'navigation',
    'custom-app-navigation': 'navigation',
    'custom-app-header': 'navigation',
    'custom-app-footer': 'navigation',
    // sl-pbux.3.1 containers
    section: 'container',
    'grid-2': 'container',
    'grid-3': 'container',
};
/** sl-pbux.3.1: Container component-types nest other instances via parentId. */
exports.CONTAINER_COMPONENT_TYPES = new Set([
    'section',
    'grid-2',
    'grid-3',
]);
function classifyComponentKind(componentType) {
    return COMPONENT_KIND_BY_TYPE[componentType] ?? 'content';
}
/**
 * sl-pbux.8 (audit-fix): The slot a top-level component ends up in is a
 * function of its componentType + the active PageShape. Exported so the
 * structural-validation engine can compute the *effective* slot for
 * components that don't carry an explicit `slotKey` (untagged) and gate the
 * slot-policy check against it.
 *
 * Always resolves to a concrete slot for persisted components.
 */
function routeToSlot(kind, shape) {
    if (kind === 'navigation') {
        return getPageShapeLayoutContract(shape).navigationSlot;
    }
    return 'main';
}
function routeComponentToSlot(componentType, shape) {
    if (componentType === 'custom-app-header') {
        return getPageShapeLayoutContract(shape).slots.includes('header')
            ? 'header'
            : 'main';
    }
    if (componentType === 'custom-app-footer') {
        return getPageShapeLayoutContract(shape).slots.includes('footer')
            ? 'footer'
            : 'main';
    }
    return routeToSlot(classifyComponentKind(componentType), shape);
}
/**
 * sl-pbux.8 (audit-fix): Pure parser for the persisted `settings.pageShape`
 * value. Returns `null` when the value is missing or not one of the three
 * supported shapes. Used by Builder surfaces that need to short-circuit
 * rendering until the empty-state picker has chosen a shape.
 */
function parsePageShape(raw) {
    if (raw === 'header' || raw === 'sidebar' || raw === 'fullwidth') {
        return raw;
    }
    return null;
}
function tagForKind(kind) {
    if (kind === 'navigation')
        return 'nav';
    if (kind === 'container')
        return 'div';
    return 'section';
}
function propsToAttrs(props) {
    const sortedKeys = Object.keys(props).sort();
    const attrs = {};
    for (const key of sortedKeys) {
        attrs[key] = (0, formula_1.valueFormula)(props[key]);
    }
    return attrs;
}
function compareForOrdering(a, b) {
    if (a.position.y !== b.position.y)
        return a.position.y - b.position.y;
    if (a.position.x !== b.position.x)
        return a.position.x - b.position.x;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
function buildElementNode(component, kind) {
    const node = {
        id: component.id,
        type: 'element',
        flavour: component.componentType,
        tag: tagForKind(kind),
        attrs: propsToAttrs(component.props),
        children: [],
    };
    if (component.customProperties != null) {
        node.customProperties = component.customProperties;
    }
    if (component.condition != null) {
        node.condition = component.condition;
    }
    if (component.repeat != null) {
        node.repeat = component.repeat;
    }
    if (component.repeatKey != null) {
        node.repeatKey = component.repeatKey;
    }
    return node;
}
function buildPageShapeRoot(shape, slotIdsInOrder) {
    return {
        id: exports.PAGE_SHAPE_ROOT_ID,
        type: 'element',
        flavour: `page-shape-${shape}`,
        tag: 'div',
        attrs: {
            class: (0, formula_1.valueFormula)(`sl-custom-app-shape-${shape}`),
            'data-shape': (0, formula_1.valueFormula)(shape),
        },
        children: [...slotIdsInOrder],
    };
}
function buildSlotNode(name, childIds) {
    return {
        id: exports.SLOT_IDS[name],
        type: 'slot',
        name,
        children: [...childIds],
    };
}
/**
 * Pure transformer: ViewConfig (flat) -> DocPageSnapshot (tree).
 *
 * Deterministic: equal inputs produce byte-identical outputs (when
 * `materializedAt` is supplied). Idempotent in the structural sense — re-
 * running with the same components and shape yields the same tree.
 */
function viewConfigToDocPageSnapshot(args) {
    const { viewConfig, pageShape, workspaceId, docId, title } = args;
    const materializedAt = args.materializedAt ?? new Date().toISOString();
    const sortedComponents = [...viewConfig.components].sort(compareForOrdering);
    const slotBuckets = {
        header: [],
        sidebar: [],
        main: [],
        footer: [],
    };
    // sl-pbux.3.1/3.5: Split components into top-level (no parentId) and
    // children (parentId set). Children are placed inside their parent's
    // ElementDocNode.children; the parent is itself a top-level instance.
    const childrenByParent = new Map();
    for (const component of sortedComponents) {
        if (component.parentId) {
            const list = childrenByParent.get(component.parentId) ?? [];
            list.push(component.id);
            childrenByParent.set(component.parentId, list);
        }
    }
    const elementNodes = [];
    for (const component of sortedComponents) {
        const kind = classifyComponentKind(component.componentType);
        const node = buildElementNode(component, kind);
        const children = childrenByParent.get(component.id);
        if (children && children.length > 0) {
            node.children = children;
        }
        elementNodes.push(node);
        if (component.parentId)
            continue; // nested instances do not own a slot
        // sl-pbux.3.5: explicit slotKey beats type-based routing.
        const slotName = component.slotKey ??
            routeComponentToSlot(component.componentType, pageShape);
        slotBuckets[slotName].push(component.id);
    }
    const slotOrder = getPageShapeLayoutContract(pageShape).slots;
    const slotIdsInOrder = slotOrder.map(name => exports.SLOT_IDS[name]);
    const root = buildPageShapeRoot(pageShape, slotIdsInOrder);
    const slotNodes = slotOrder.map(name => buildSlotNode(name, slotBuckets[name]));
    const nodes = {};
    nodes[root.id] = root;
    for (const slot of slotNodes) {
        nodes[slot.id] = slot;
    }
    for (const el of elementNodes) {
        nodes[el.id] = el;
    }
    return {
        snapshotVersion: 1,
        contentType: 'doc_page',
        workspaceId,
        docId,
        rootNodeId: exports.PAGE_SHAPE_ROOT_ID,
        title,
        materializedAt,
        nodes,
    };
}
